import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import ZoomIn from "@material-ui/icons/ZoomIn";
import RotateLeft from "@material-ui/icons/RotateLeft";
import RotateRight from "@material-ui/icons/RotateRight";
import FolderShared from "@material-ui/icons/FolderShared";
import { API_ROOT } from "../api-config";
import {
  compressDocumentImage,
  isNetworkUploadError,
} from "../utils/compressDocumentImage";
import { canManageClientDocuments } from "../rbac";

const ACCEPT = "image/*,application/pdf";
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.pdf']
const ALLOWED_MIME = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
  'application/x-pdf',
]

function isAllowedDocumentFile(file) {
  if (!file) return false
  const type = String(file.type || '')
    .toLowerCase()
    .split(';')[0]
    .trim()
  if (ALLOWED_MIME.indexOf(type) !== -1) return true
  const name = String(file.name || '').toLowerCase()
  const dot = name.lastIndexOf('.')
  const ext = dot >= 0 ? name.slice(dot) : ''
  if (ALLOWED_EXT.indexOf(ext) !== -1) return true
  if (!type && (!ext || name === 'image' || name === 'blob')) return true
  return false
}

function isPdfBlob(blob) {
  if (!blob) return false;
  const type = String(blob.type || "")
    .toLowerCase()
    .split(";")[0]
    .trim();
  return type === "application/pdf" || type === "application/x-pdf";
}

function normalizeQuarterTurns(degrees) {
  return ((degrees % 360) + 360) % 360;
}

function rotateImageBlob(blob, degrees) {
  const normalized = normalizeQuarterTurns(degrees);
  if (!normalized) return Promise.resolve(blob);

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const swap = normalized === 90 || normalized === 270;
      const canvas = document.createElement("canvas");
      canvas.width = swap ? img.naturalHeight : img.naturalWidth;
      canvas.height = swap ? img.naturalWidth : img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx || !canvas.width || !canvas.height) {
        reject(new Error("Could not rotate image"));
        return;
      }
      const mime = blob.type === "image/png" ? "image/png" : "image/jpeg";
      if (mime === "image/jpeg") {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((normalized * Math.PI) / 180);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      canvas.toBlob(
        out => {
          if (!out) reject(new Error("Could not rotate image"));
          else resolve(out);
        },
        mime,
        0.92
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not rotate image"));
    };
    img.src = url;
  });
}

function ImageRotateActions({
  onRotate,
  onSave,
  canSave,
  saving,
  rotated,
  disabled,
}) {
  return (
    <React.Fragment>
      <Tooltip title="Rotate left">
        <span>
          <IconButton
            aria-label="Rotate left"
            onClick={() => onRotate(-90)}
            disabled={disabled}
            size="small"
          >
            <RotateLeft fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Rotate right">
        <span>
          <IconButton
            aria-label="Rotate right"
            onClick={() => onRotate(90)}
            disabled={disabled}
            size="small"
          >
            <RotateRight fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {canSave && rotated ? (
        <Button color="primary" onClick={onSave} disabled={disabled || saving}>
          {saving ? "Saving orientation…" : "Save orientation"}
        </Button>
      ) : null}
    </React.Fragment>
  );
}

const hiddenFileInputStyle = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  border: 0,
  opacity: 0,
};

function documentsQuery(target) {
  if (target && target.rentalId) return `rentalId=${target.rentalId}`;
  if (target && target.clientId) return `clientId=${target.clientId}`;
  return "";
}

function hasDocumentTarget(target) {
  return !!(target && (target.rentalId || target.clientId));
}

async function fetchDocumentStatus(target) {
  const token = localStorage.getItem("token");
  const q = documentsQuery(target);
  if (!q) return null;
  const url = `${API_ROOT}/client_documents/status?${q}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });
  if (!res.ok) return null;
  return res.json();
}

async function fetchDocumentBlob(target, type) {
  const token = localStorage.getItem("token");
  const url = target.rentalId
    ? `${API_ROOT}/client_documents/file/rental/${target.rentalId}/${type}`
    : `${API_ROOT}/client_documents/file/${target.clientId}/${type}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  return res.blob();
}

async function uploadDocument(target, type, file) {
  const token = localStorage.getItem("token");
  const form = new FormData();
  form.append("type", type);
  form.append("file", file);
  if (target.rentalId) form.append("rentalId", String(target.rentalId));
  else if (target.clientId) form.append("clientId", String(target.clientId));

  let res;
  try {
    res = await fetch(`${API_ROOT}/client_documents`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
  } catch (err) {
    return { ok: false, error: (err && err.message) || "Failed to fetch" };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data.error || data.message || "Upload failed" };
  }
  return { ok: true, rentalsUpdated: data.rentalsUpdated, status: data };
}

async function deleteDocument(target, type) {
  const token = localStorage.getItem("token");
  const q = documentsQuery(target);
  const res = await fetch(
    `${API_ROOT}/client_documents/${encodeURIComponent(type)}?${q}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data.error || "Delete failed" };
  }
  return { ok: true, status: data };
}

const overlayIconStyle = {
  position: "absolute",
  top: 8,
  background: "rgba(255,255,255,0.95)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
};

function DocumentViewerDialog({
  open,
  title,
  previewUrl,
  isPdf,
  onClose,
  onRotate,
  onSaveOrientation,
  canSaveOrientation,
  savingOrientation,
  rotated,
}) {
  if (!previewUrl) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      onClick={e => e.stopPropagation()}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {isPdf ? (
          <iframe
            title={title}
            src={previewUrl}
            style={{
              width: "100%",
              height: "78vh",
              border: "1px solid #e0e0e0",
              borderRadius: 4,
              background: "#fff",
            }}
          />
        ) : (
          <img
            src={previewUrl}
            alt={title}
            style={{
              display: "block",
              width: "100%",
              maxHeight: "78vh",
              objectFit: "contain",
              background: "#111",
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        {!isPdf && onRotate ? (
          <ImageRotateActions
            onRotate={onRotate}
            onSave={onSaveOrientation}
            canSave={canSaveOrientation}
            saving={savingOrientation}
            rotated={rotated}
            disabled={savingOrientation}
          />
        ) : null}
        <Button
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
        >
          Open in new tab
        </Button>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DocumentSlot({
  title,
  titleSuffix,
  hint,
  type,
  blob,
  uploaded,
  uploading,
  deleting,
  onUpload,
  onDelete,
  canEdit = true,
  md = 6,
}) {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBroken, setPreviewBroken] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [rotatedUrl, setRotatedUrl] = useState(null);
  const [savingOrientation, setSavingOrientation] = useState(false);
  const [rotateError, setRotateError] = useState(null);
  const inputId = `staff-doc-input-${type}`;
  const isPdf = isPdfBlob(blob);
  const displayUrl = rotation && rotatedUrl ? rotatedUrl : previewUrl;

  useEffect(() => {
    setRotation(0);
    setRotateError(null);
    setRotatedUrl(null);
  }, [blob]);

  useEffect(() => {
    if (!blob) {
      setPreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(blob);
    setPreviewUrl(objectUrl);
    setPreviewBroken(false);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  useEffect(() => {
    if (!blob || isPdf || !rotation) {
      setRotatedUrl(null);
      return undefined;
    }
    let cancelled = false;
    rotateImageBlob(blob, rotation)
      .then(out => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(out);
        setRotatedUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setRotatedUrl(null);
          setRotateError("Could not rotate this image.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [blob, isPdf, rotation]);

  useEffect(() => {
    return () => {
      if (rotatedUrl) URL.revokeObjectURL(rotatedUrl);
    };
  }, [rotatedUrl]);

  const turnImage = delta => {
    setRotateError(null);
    setRotation(current => normalizeQuarterTurns(current + delta));
  };

  const saveOrientation = async () => {
    if (!blob || !rotation || isPdf || !canEdit || !onUpload) return;
    setSavingOrientation(true);
    setRotateError(null);
    try {
      const out = await rotateImageBlob(blob, rotation);
      const ext = out.type === "image/png" ? "png" : "jpg";
      const file = new File([out], `document.${ext}`, {
        type: out.type || "image/jpeg",
      });
      await onUpload(type, file);
    } catch (err) {
      setRotateError("Could not save the rotated image.");
    } finally {
      setSavingOrientation(false);
    }
  };

  const pickFile = files => {
    if (!files || !files.length) return null;
    const file = files[0];
    if (!isAllowedDocumentFile(file)) return null;
    return file;
  };

  const submit = file => {
    if (!canEdit) return;
    if (file && !uploading && !deleting) onUpload(type, file);
  };

  const openViewer = e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (previewUrl && !previewBroken) setViewerOpen(true);
  };

  const viewBtn = previewUrl && !previewBroken ? (
    <Tooltip title="View larger">
      <span>
        <IconButton
          aria-label="View larger"
          onClick={openViewer}
          size="small"
          style={{
            ...overlayIconStyle,
            left: 8,
            color: "#191c1e",
          }}
        >
          <ZoomIn fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  ) : null;

  const deleteBtn = uploaded && onDelete ? (
    <Tooltip title="Delete uploaded document">
      <span>
        <IconButton
          aria-label="Delete uploaded document"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(type);
          }}
          disabled={uploading || deleting}
          size="small"
          style={{
            ...overlayIconStyle,
            right: 8,
            color: "#d32f2f",
          }}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  ) : null;

  return (
    <Grid item xs={12} md={md}>
      <div>
      <Typography
        variant="subtitle1"
        style={{
          fontWeight: 700,
          marginBottom: 4,
          color: "#fab028",
          lineHeight: 1.35,
        }}
      >
        {title}
        {titleSuffix ? (
          <span style={{ color: "#d32f2f", fontWeight: 700 }}>
            {" "}
            {titleSuffix}
          </span>
        ) : null}
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginBottom: 8 }}
      >
        {hint}
      </Typography>
      <Typography
        variant="caption"
        style={{
          display: "inline-block",
          marginBottom: 8,
          padding: "2px 8px",
          borderRadius: 4,
          background: uploaded ? "#e8f5e9" : "#fff3e0",
          color: uploaded ? "#2e7d32" : "#ef6c00",
        }}
      >
        {uploaded ? "Uploaded" : "Not uploaded yet"}
      </Typography>

      {previewUrl && isPdf ? (
        <div style={{ marginBottom: 12, position: "relative" }}>
          <iframe
            title={title}
            src={previewUrl}
            style={{
              width: "100%",
              height: 220,
              border: "1px solid #e0e0e0",
              borderRadius: 4,
            }}
          />
          {viewBtn}
          {/* {deleteBtn} */}
          <div style={{ marginTop: 6 }}>
            <Button color="primary" onClick={openViewer} style={{ minWidth: 0, padding: 0 }}>
              View larger
            </Button>
            {" · "}
            <a href={previewUrl} target="_blank" rel="noopener noreferrer">
              Open PDF
            </a>
          </div>
        </div>
      ) : displayUrl && !previewBroken ? (
        <div style={{ marginBottom: 12, maxWidth: "100%" }}>
          <div style={{ position: "relative", display: "inline-block", maxWidth: "100%" }}>
            <img
              src={displayUrl}
              alt={title}
              onError={() => setPreviewBroken(true)}
              onDoubleClick={openViewer}
              title="Double-click to view larger"
              style={{
                maxWidth: "100%",
                maxHeight: 220,
                borderRadius: 4,
                display: "block",
                border: "1px solid #e0e0e0",
                cursor: "zoom-in",
              }}
            />
            {viewBtn}
            {/* {deleteBtn} */}
          </div>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
            <ImageRotateActions
              onRotate={turnImage}
              onSave={saveOrientation}
              canSave={canEdit}
              saving={savingOrientation || uploading || deleting}
              rotated={!!rotation}
              disabled={uploading || deleting || savingOrientation}
            />
          </div>
          {rotateError ? (
            <Typography variant="caption" style={{ color: "#c62828", display: "block" }}>
              {rotateError}
            </Typography>
          ) : rotation && !canEdit ? (
            <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
              Orientation is for viewing only.
            </Typography>
          ) : null}
        </div>
      ) : uploaded ? (
        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
          Document uploaded. Preview is not available on this device.
        </Typography>
      ) : null}

      {uploaded && onDelete && canEdit ? (
        <Button
          color="secondary"
          onClick={() => onDelete(type)}
          disabled={uploading || deleting}
          style={{ marginBottom: 12, color: "#d32f2f" }}
        >
          <DeleteOutline style={{ marginRight: 6 }} />
          {deleting ? "Deleting…" : "Delete file"}
        </Button>
      ) : null}

      {canEdit ? (
      <label
        htmlFor={inputId}
        onDragEnter={e => {
          e.preventDefault();
          e.stopPropagation();
          if (!uploading) setIsDragOver(true);
        }}
        onDragOver={e => {
          e.preventDefault();
          e.stopPropagation();
          if (!uploading) setIsDragOver(true);
        }}
        onDragLeave={e => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragOver(false);
        }}
        onDrop={e => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragOver(false);
          submit(pickFile(e.dataTransfer.files));
        }}
        onClick={e => {
          if (uploading) e.preventDefault();
        }}
        style={{
          display: "block",
          border: `2px dashed ${isDragOver ? "#1976d2" : "#bdbdbd"}`,
          borderRadius: 8,
          padding: "20px 12px",
          textAlign: "center",
          cursor: uploading ? "wait" : "pointer",
          background: isDragOver ? "#e3f2fd" : "#fafafa",
          opacity: uploading ? 0.7 : 1,
          minHeight: 48,
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          style={hiddenFileInputStyle}
          disabled={uploading}
          onChange={e => {
            submit(pickFile(e.target.files));
            e.target.value = "";
          }}
        />
        {uploading ? (
          <CircularProgress size={24} />
        ) : (
          <React.Fragment>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {uploaded ? "Replace file" : "Upload file"}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              JPG, PNG, WEBP, HEIC or PDF · max 10 MB · tap to take a photo or choose a file
            </Typography>
          </React.Fragment>
        )}
      </label>
      ) : null}
      </div>
      <DocumentViewerDialog
        open={viewerOpen}
        title={title}
        previewUrl={displayUrl}
        isPdf={isPdf}
        onClose={() => setViewerOpen(false)}
        onRotate={turnImage}
        onSaveOrientation={saveOrientation}
        canSaveOrientation={canEdit}
        savingOrientation={savingOrientation || uploading || deleting}
        rotated={!!rotation}
      />
    </Grid>
  );
}

const ClientDocumentsModal = ({ open, onClose, clientId, rentalId, allowUpload = true }) => {
  const staffCanManage = canManageClientDocuments();
  const canEdit = !!(allowUpload && staffCanManage);
  const target = { clientId, rentalId };
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [passportBlob, setPassportBlob] = useState(null);
  const [licenseBlob, setLicenseBlob] = useState(null);
  const [passengerPassportBlob, setPassengerPassportBlob] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);
  const [deletingType, setDeletingType] = useState(null);
  const [message, setMessage] = useState(null);

  const loadDocuments = useCallback(async () => {
    if (!hasDocumentTarget(target)) return;
    setLoading(true);
    setPassportBlob(null);
    setLicenseBlob(null);
    setPassengerPassportBlob(null);
    setMessage(null);
    try {
      const docStatus = await fetchDocumentStatus(target);
      setStatus(docStatus);
      if (docStatus && docStatus.passport) {
        const blob = await fetchDocumentBlob(target, "passport");
        setPassportBlob(blob);
      }
      if (docStatus && docStatus.driving_licence) {
        const blob = await fetchDocumentBlob(target, "driving_licence");
        setLicenseBlob(blob);
      }
      if (docStatus && docStatus.requiresPassengerPassport && docStatus.passenger_passport) {
        const blob = await fetchDocumentBlob(target, "passenger_passport");
        setPassengerPassportBlob(blob);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, rentalId]);

  useEffect(() => {
    if (open && hasDocumentTarget(target)) {
      loadDocuments();
    }
  }, [open, clientId, rentalId, loadDocuments]);

  const handleUpload = async (type, file) => {
    if (!hasDocumentTarget(target)) return;
    if (file.size > MAX_BYTES) {
      setMessage({ severity: "error", text: "File is too large (max 10 MB)." });
      return;
    }
    setUploadingType(type);
    setMessage(null);
    let result = await uploadDocument(target, type, file);
    if (
      !result.ok &&
      isNetworkUploadError(result.error) &&
      file.size > 400 * 1024
    ) {
      try {
        const compacted = await compressDocumentImage(file);
        if (compacted.size < file.size) {
          result = await uploadDocument(target, type, compacted);
        }
      } catch (retryErr) {
        result = {
          ok: false,
          error:
            (retryErr && retryErr.message) ||
            result.error ||
            "Could not upload the document.",
        };
      }
    }
    setUploadingType(null);
    if (!result.ok) {
      setMessage({
        severity: "error",
        text: result.error || "Could not upload the document.",
      });
      return;
    }
    await loadDocuments();
    setMessage({
      severity: "success",
      text: result.rentalsUpdated
        ? rentalId
          ? "Document saved. This rental marked as documentation received."
          : "Document saved. Client rentals marked as documentation received."
        : "Document saved successfully.",
    });
  };

  const handleDelete = async type => {
    if (!hasDocumentTarget(target) || !canEdit) return;
    if (!window.confirm("Delete this document? You can upload a new file afterwards.")) {
      return;
    }
    setDeletingType(type);
    setMessage(null);
    const result = await deleteDocument(target, type);
    setDeletingType(null);
    if (!result.ok) {
      setMessage({
        severity: "error",
        text: result.error || "Could not delete the document.",
      });
      return;
    }
    await loadDocuments();
    setMessage({
      severity: "success",
      text: "Document deleted. You can upload a new one.",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{rentalId ? "Rider documents" : "Client documents"}</DialogTitle>
      <DialogContent>
        {!hasDocumentTarget(target) ? (
          <Typography variant="body2" color="textSecondary">
            No client selected.
          </Typography>
        ) : loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "32px 0",
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <React.Fragment>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              {canEdit
                ? "Upload, replace or delete passport/ID and driving licence (images or PDF, max 10 MB). Only IMT staff can change files already stored in Error."
                : "View client documents. Only IMT staff (Super Admin, Agent and other IMT roles) can upload, replace or delete files."}
            </Typography>
            {message ? (
              <Typography
                variant="body2"
                style={{
                  marginBottom: 12,
                  color: message.severity === "error" ? "#c62828" : "#2e7d32",
                }}
              >
                {message.text}
              </Typography>
            ) : null}
            <Grid container spacing={3}>
              <DocumentSlot
                title="Passport / ID"
                hint="Legible photo or PDF of passport or national ID."
                type="passport"
                blob={passportBlob}
                uploaded={!!(status && status.passport)}
                uploading={uploadingType === "passport"}
                deleting={deletingType === "passport"}
                onUpload={canEdit ? handleUpload : () => {}}
                onDelete={canEdit ? handleDelete : undefined}
                canEdit={canEdit}
                md={status && status.requiresPassengerPassport ? 4 : 6}
              />
              <DocumentSlot
                title="Driving licence"
                hint="Legible photo or PDF of the original driving licence."
                type="driving_licence"
                blob={licenseBlob}
                uploaded={!!(status && status.driving_licence)}
                uploading={uploadingType === "driving_licence"}
                deleting={deletingType === "driving_licence"}
                onUpload={canEdit ? handleUpload : () => {}}
                onDelete={canEdit ? handleDelete : undefined}
                canEdit={canEdit}
                md={status && status.requiresPassengerPassport ? 4 : 6}
              />
              {status && status.requiresPassengerPassport ? (
                <DocumentSlot
                  title="Passenger passport"
                  titleSuffix="(Tour Passengers only)"
                  hint="Legible photo or PDF of the passenger passport or national ID."
                  type="passenger_passport"
                  blob={passengerPassportBlob}
                  uploaded={!!(status && status.passenger_passport)}
                  uploading={uploadingType === "passenger_passport"}
                  deleting={deletingType === "passenger_passport"}
                  onUpload={canEdit ? handleUpload : () => {}}
                  onDelete={canEdit ? handleDelete : undefined}
                  canEdit={canEdit}
                  md={4}
                />
              ) : null}
            </Grid>
          </React.Fragment>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientDocumentsModal;

export function ClientDocumentsIconButton({ clientId, rentalId }) {
  const [open, setOpen] = useState(false);
  if (!clientId && !rentalId) return null;
  return (
    <React.Fragment>
      <span
        title="View / upload client documents"
        alt="View / upload client documents"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        style={{
          cursor: "pointer",
          marginLeft: 4,
          display: "inline-flex",
          verticalAlign: "middle",
          color: "#555",
        }}
      >
        <FolderShared style={{ fontSize: 18 }} />
      </span>
      <ClientDocumentsModal
        open={open}
        onClose={() => setOpen(false)}
        clientId={clientId}
        rentalId={rentalId}
      />
    </React.Fragment>
  );
}



// WEBPACK FOOTER //
// ./src/components/ClientDocumentsModal.js